import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { AppSyncProvider } from './contexts/AppSyncContext';
import { PrivateRoute } from './components/PrivateRoute';
import { UpdateAlertModal } from './components/UpdateAlertModal';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { TimeCheckAlert } from './components/TimeCheckAlert';
import { LoginPage } from './pages/LoginPage';
import axios from 'axios';
import '@/App.css';

// Core dashboards - lazy loaded (only one is used at a time based on role)
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const ManagerDashboard = lazy(() => import('./pages/ManagerDashboard').then(m => ({ default: m.ManagerDashboard })));
const CoordinateurGeneralDashboard = lazy(() => import('./pages/CoordinateurGeneralDashboard').then(m => ({ default: m.CoordinateurGeneralDashboard })));
const CoordinateurZoneDashboard = lazy(() => import('./pages/CoordinateurZoneDashboard').then(m => ({ default: m.CoordinateurZoneDashboard })));
const LivreurDashboard = lazy(() => import('./pages/LivreurDashboard').then(m => ({ default: m.LivreurDashboard })));

// Lazy loaded pages - improves initial bundle size
const ProductsPage = lazy(() => import('./pages/ProductsPage').then(m => ({ default: m.ProductsPage })));
const ZonesPage = lazy(() => import('./pages/ZonesPage').then(m => ({ default: m.ZonesPage })));
const MyOrdersPage = lazy(() => import('./pages/MyOrdersPage').then(m => ({ default: m.MyOrdersPage })));
const AdminOrdersPage = lazy(() => import('./pages/AdminOrdersPage').then(m => ({ default: m.AdminOrdersPage })));
const UsersPage = lazy(() => import('./pages/UsersPage').then(m => ({ default: m.UsersPage })));
const DepotsPage = lazy(() => import('./pages/DepotsPage').then(m => ({ default: m.DepotsPage })));
const DepotDetailPage = lazy(() => import('./pages/DepotDetailPage'));
const MyCommissionsPage = lazy(() => import('./pages/MyCommissionsPage').then(m => ({ default: m.MyCommissionsPage })));
const CommissionsAdminPage = lazy(() => import('./pages/CommissionsAdminPage').then(m => ({ default: m.CommissionsAdminPage })));
const CommissionConfigPage = lazy(() => import('./pages/CommissionConfigPage').then(m => ({ default: m.CommissionConfigPage })));
const StockPage = lazy(() => import('./pages/StockPage').then(m => ({ default: m.StockPage })));
const MyAffiliatesPage = lazy(() => import('./pages/MyAffiliatesPage').then(m => ({ default: m.MyAffiliatesPage })));
const AffiliateNetworkPage = lazy(() => import('./pages/AffiliateNetworkPage').then(m => ({ default: m.AffiliateNetworkPage })));
const StockAlertsPage = lazy(() => import('./pages/StockAlertsPage').then(m => ({ default: m.StockAlertsPage })));
const StockDirectOutPage = lazy(() => import('./pages/StockDirectOutPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const ReportsPage = lazy(() => import('./pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
const ClientsPage = lazy(() => import('./pages/ClientsPage').then(m => ({ default: m.ClientsPage })));
const ExpensesPage = lazy(() => import('./pages/ExpensesPage').then(m => ({ default: m.ExpensesPage })));
const SuppliersPage = lazy(() => import('./pages/SuppliersPage').then(m => ({ default: m.SuppliersPage })));
const TreasuryPage = lazy(() => import('./pages/TreasuryPage').then(m => ({ default: m.TreasuryPage })));
const FinancialDashboardPage = lazy(() => import('./pages/FinancialDashboardPage').then(m => ({ default: m.FinancialDashboardPage })));
const FinancialAlertsPage = lazy(() => import('./pages/FinancialAlertsPage').then(m => ({ default: m.FinancialAlertsPage })));
const ShipmentTrackingPage = lazy(() => import('./pages/ShipmentTrackingPage').then(m => ({ default: m.ShipmentTrackingPage })));
const ProductCatalogPage = lazy(() => import('./pages/ProductCatalogPage').then(m => ({ default: m.ProductCatalogPage })));
const AccountingPage = lazy(() => import('./pages/AccountingPage').then(m => ({ default: m.AccountingPage })));
const StockMovementsPage = lazy(() => import('./pages/StockMovementsPage').then(m => ({ default: m.StockMovementsPage })));
const DocumentsArchivePage = lazy(() => import('./pages/DocumentsArchivePage').then(m => ({ default: m.DocumentsArchivePage })));
const ReceivablesDashboardPage = lazy(() => import('./pages/ReceivablesDashboardPage').then(m => ({ default: m.ReceivablesDashboardPage })));
const DepotDashboardPage = lazy(() => import('./pages/DepotDashboardPage').then(m => ({ default: m.DepotDashboardPage })));
const CatalogPublicPage = lazy(() => import('./pages/CatalogPublicPage').then(m => ({ default: m.CatalogPublicPage })));
const AnalyticsDashboardPage = lazy(() => import('./pages/AnalyticsDashboardPage').then(m => ({ default: m.AnalyticsDashboardPage })));
const DepositsPage = lazy(() => import('./pages/DepositsPage').then(m => ({ default: m.DepositsPage })));
const OrderApprovalsPage = lazy(() => import('./pages/OrderApprovalsPage').then(m => ({ default: m.OrderApprovalsPage })));
const ApprovalRequestsPage = lazy(() => import('./pages/ApprovalRequestsPage'));
const OperationalReportsPage = lazy(() => import('./pages/OperationalReportsPage'));
const CataloguePage = lazy(() => import('./pages/CataloguePage').then(m => ({ default: m.CataloguePage })));
const StockAdvancedPage = lazy(() => import('./pages/StockAdvancedPage').then(m => ({ default: m.StockAdvancedPage })));
const PurchasingPage = lazy(() => import('./pages/PurchasingPage').then(m => ({ default: m.PurchasingPage })));
const BonAchatPage = lazy(() => import('./pages/BonAchatPage').then(m => ({ default: m.BonAchatPage })));
const SuiviColisPage = lazy(() => import('./pages/SuiviColisPage').then(m => ({ default: m.SuiviColisPage })));
const RestockAlertsPage = lazy(() => import('./pages/RestockAlertsPage').then(m => ({ default: m.RestockAlertsPage })));
const ProductReturnsPage = lazy(() => import('./pages/ProductReturnsPage').then(m => ({ default: m.ProductReturnsPage })));
const ProfileManagementPage = lazy(() => import('./pages/ProfileManagementPage').then(m => ({ default: m.ProfileManagementPage })));
const SalesAnalyticsPage = lazy(() => import('./pages/SalesAnalyticsPage').then(m => ({ default: m.SalesAnalyticsPage })));
const CaissesPage = lazy(() => import('./pages/CaissesPage').then(m => ({ default: m.CaissesPage })));
const BanquesPage = lazy(() => import('./pages/BanquesPage').then(m => ({ default: m.BanquesPage })));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage').then(m => ({ default: m.AuditLogPage })));
const AdBudgetPage = lazy(() => import('./pages/AdBudgetPage').then(m => ({ default: m.AdBudgetPage })));
const SMSCampaignsPage = lazy(() => import('./pages/SMSCampaignsPage').then(m => ({ default: m.SMSCampaignsPage })));
const CommissionPaymentsPage = lazy(() => import('./pages/CommissionPaymentsPage').then(m => ({ default: m.CommissionPaymentsPage })));
const RolePermissionsPage = lazy(() => import('./pages/RolePermissionsPage').then(m => ({ default: m.RolePermissionsPage })));
const ManualDeliveriesPage = lazy(() => import('./pages/ManualDeliveriesPage').then(m => ({ default: m.ManualDeliveriesPage })));
const PayrollPage = lazy(() => import('./pages/PayrollPage').then(m => ({ default: m.PayrollPage })));
const WeeklyReportsPage = lazy(() => import('./pages/WeeklyReportsPage').then(m => ({ default: m.WeeklyReportsPage })));
const ChatPage = lazy(() => import('./pages/ChatPage').then(m => ({ default: m.ChatPage })));
const VIPDiscountPage = lazy(() => import('./pages/VIPDiscountPage').then(m => ({ default: m.VIPDiscountPage })));
const UpdatesManagementPage = lazy(() => import('./pages/UpdatesManagementPage').then(m => ({ default: m.UpdatesManagementPage })));
const PushNotificationsPage = lazy(() => import('./pages/PushNotificationsPage'));
const StockPredictionPage = lazy(() => import('./pages/StockPredictionPage').then(m => ({ default: m.StockPredictionPage })));
const PerformanceDashboardPage = lazy(() => import('./pages/PerformanceDashboardPage').then(m => ({ default: m.PerformanceDashboardPage })));
const MediaCreatifPage = lazy(() => import('./pages/MediaCreatifPage').then(m => ({ default: m.MediaCreatifPage })));
const MaBoutiquePage = lazy(() => import('./pages/MaBoutiquePage').then(m => ({ default: m.MaBoutiquePage })));
const PublicStorePage = lazy(() => import('./pages/PublicStorePage').then(m => ({ default: m.PublicStorePage })));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage').then(m => ({ default: m.ProductDetailPage })));
const LoyaltyPage = lazy(() => import('./pages/LoyaltyPage').then(m => ({ default: m.LoyaltyPage })));
const SellerDiscountDashboardPage = lazy(() => import('./pages/SellerDiscountDashboardPage'));
const SellerOrderTrackingPage = lazy(() => import('./pages/SellerOrderTrackingPage'));
const PayslipSettingsPage = lazy(() => import('./pages/PayslipSettingsPage'));
const PayslipArchivePage = lazy(() => import('./pages/PayslipArchivePage'));
const AccountingOhadaPage = lazy(() => import('./pages/AccountingOhadaPage'));
const KPIDashboardPage = lazy(() => import('./pages/KPIDashboardPage').then(m => ({ default: m.KPIDashboardPage })));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage').then(m => ({ default: m.AdminDashboardPage })));
const AdvertisingBudgetPage = lazy(() => import('./pages/AdvertisingBudgetPage').then(m => ({ default: m.AdvertisingBudgetPage })));
const SmartCatalogPage = lazy(() => import('./pages/SmartCatalogPage').then(m => ({ default: m.SmartCatalogPage })));
const SellerDashboardPage = lazy(() => import('./pages/SellerDashboardPage'));
const FeedbackManagementPage = lazy(() => import('./pages/FeedbackManagementPage').then(m => ({ default: m.FeedbackManagementPage })));
const AIToolsPage = lazy(() => import('./pages/AIToolsPage').then(m => ({ default: m.AIToolsPage })));
const AdminDocumentsPage = lazy(() => import('./pages/AdminDocumentsPage').then(m => ({ default: m.AdminDocumentsPage })));
const ProfitCalculatorPage = lazy(() => import('./pages/ProfitCalculatorPage'));
const RolesPermissionsPage = lazy(() => import('./pages/RolesPermissionsPage'));
const SellerPerformanceMonitorPage = lazy(() => import('./pages/SellerPerformanceMonitorPage'));
const SalesPeakAlertsPage = lazy(() => import('./pages/SalesPeakAlertsPage'));
const GlobalPixelConfigPage = lazy(() => import('./pages/GlobalPixelConfigPage'));
const InventoryScannerPage = lazy(() => import('./pages/InventoryScannerPage'));
const ZoneStockControlPage = lazy(() => import('./pages/ZoneStockControlPage'));
const EditRequestsPage = lazy(() => import('./pages/EditRequestsPage'));
const RecoveryPlansPage = lazy(() => import('./pages/RecoveryPlansPage'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Loading component for suspense
const PageLoader = () => (
  <div className="min-h-screen flex flex-col bg-slate-50" data-testid="page-loader">
    {/* Skeleton header */}
    <div className="h-14 sm:h-16 bg-white/60 backdrop-blur border-b border-slate-200 flex items-center px-4 sm:px-6 gap-3">
      <div className="h-8 w-8 rounded-md bg-slate-200 animate-pulse" />
      <div className="h-4 w-40 sm:w-56 rounded bg-slate-200 animate-pulse" />
      <div className="ml-auto h-8 w-24 sm:w-32 rounded bg-slate-200 animate-pulse" />
    </div>
    {/* Skeleton body */}
    <div className="flex-1 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-24 rounded-lg bg-white shadow-sm border border-slate-200 overflow-hidden">
              <div className="h-full w-full bg-slate-100 animate-pulse" />
            </div>
          ))}
        </div>
        <div className="rounded-lg bg-white shadow-sm border border-slate-200 p-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-4 rounded bg-slate-100 animate-pulse" style={{ width: `${60 + (i * 7) % 40}%` }} />
          ))}
        </div>
      </div>
      <div className="mt-6 flex items-center justify-center gap-2 text-slate-400 text-xs">
        <div className="w-3 h-3 border-2 border-violet-200 border-t-violet-500 rounded-full animate-spin" />
        Chargement...
      </div>
    </div>
  </div>
);

const DashboardRouter = () => {
  const { user } = useAuth();

  if (!user) return <Navigate to="/login" replace />;

  const roleBasedDashboard = {
    admin: <AdminDashboard />,
    comptable: <AdminDashboard />,
    manager: <ManagerDashboard />,
    coordinateur_general: <CoordinateurGeneralDashboard />,
    coordinateur_zone: <CoordinateurZoneDashboard />,
    livreur: <LivreurDashboard />,
    responsable_stock: <AdminDashboard />
  };

  return roleBasedDashboard[user.role] || <AdminDashboard />;
};

function AppContent() {
  useEffect(() => {
    initializeDemoData();
  }, []);

  const initializeDemoData = async () => {
    try {
      await axios.post(`${API}/init-demo-data`);
    } catch (error) {
      console.log('Demo data initialization skipped or already exists');
    }
  };

  return (
    <div className="App">
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          
          {/* Public Store Page */}
          <Route path="/boutique/:slug" element={<PublicStorePage />} />
          <Route path="/boutique/:slug/produit/:productId" element={<ProductDetailPage />} />
        
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <DashboardRouter />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/products"
          element={
            <PrivateRoute allowedRoles={['admin', 'responsable_stock']}>
              <ProductsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/zones"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <ZonesPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/my-orders"
          element={
            <PrivateRoute allowedRoles={['manager']}>
              <MyOrdersPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/orders"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <AdminOrdersPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/users"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <UsersPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/roles-permissions"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <RolesPermissionsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/seller-monitoring"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <SellerPerformanceMonitorPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/sales-peak-alerts"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <SalesPeakAlertsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/global-pixels"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <GlobalPixelConfigPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/depots"
          element={
            <PrivateRoute allowedRoles={['admin', 'responsable_stock']}>
              <DepotsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/depots/:depotId"
          element={
            <PrivateRoute allowedRoles={['admin', 'responsable_stock', 'coordinateur_zone', 'coordinateur_general', 'magasinier']}>
              <DepotDetailPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/inventory"
          element={
            <PrivateRoute allowedRoles={['admin', 'responsable_stock']}>
              <InventoryScannerPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/zone-stock-control"
          element={
            <PrivateRoute allowedRoles={['admin', 'coordinateur_zone']}>
              <ZoneStockControlPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/edit-requests"
          element={
            <PrivateRoute>
              <EditRequestsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/recovery-plans"
          element={
            <PrivateRoute>
              <RecoveryPlansPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/my-commissions"
          element={
            <PrivateRoute allowedRoles={['manager', 'coordinateur_general', 'coordinateur_zone', 'livreur']}>
              <MyCommissionsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/commissions-admin"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <CommissionsAdminPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/stock"
          element={
            <PrivateRoute allowedRoles={['admin', 'responsable_stock', 'coordinateur_zone', 'magasinier']}>
              <StockPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/stock-advanced"
          element={
            <PrivateRoute allowedRoles={['admin', 'responsable_stock']}>
              <StockAdvancedPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/commission-config"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <CommissionConfigPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/my-affiliates"
          element={
            <PrivateRoute allowedRoles={['manager']}>
              <MyAffiliatesPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/affiliate-network"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <AffiliateNetworkPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/stock-alerts"
          element={
            <PrivateRoute allowedRoles={['admin', 'responsable_stock', 'coordinateur_zone', 'magasinier']}>
              <StockAlertsPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/stock-direct-out"
          element={
            <PrivateRoute allowedRoles={['admin', 'responsable_stock']}>
              <StockDirectOutPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/settings"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <SettingsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/role-permissions"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <RolePermissionsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/reports"
          element={
            <PrivateRoute allowedRoles={['admin', 'manager', 'coordinateur_general', 'coordinateur_zone']}>
              <ReportsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/clients"
          element={
            <PrivateRoute allowedRoles={['admin', 'manager']}>
              <ClientsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/product-returns"
          element={
            <PrivateRoute allowedRoles={['admin', 'manager']}>
              <ProductReturnsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/expenses"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <ExpensesPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/suppliers"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <SuppliersPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/treasury"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <TreasuryPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/financial-dashboard"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <FinancialDashboardPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/financial-alerts"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <FinancialAlertsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/shipments"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <ShipmentTrackingPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/catalog"
          element={
            <PrivateRoute allowedRoles={['admin', 'manager']}>
              <ProductCatalogPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/accounting"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <AccountingPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/profit-calculator"
          element={
            <PrivateRoute allowedRoles={['admin', 'comptable']}>
              <ProfitCalculatorPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/stock-movements"
          element={
            <PrivateRoute allowedRoles={['admin', 'responsable_stock', 'magasinier']}>
              <StockMovementsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/documents-archive"
          element={
            <PrivateRoute allowedRoles={['admin', 'responsable_stock']}>
              <DocumentsArchivePage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/purchasing"
          element={
            <PrivateRoute allowedRoles={['admin', 'responsable_stock']}>
              <PurchasingPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/bon-achat"
          element={
            <PrivateRoute allowedRoles={['admin', 'responsable_stock']}>
              <BonAchatPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/suivi-colis"
          element={
            <PrivateRoute allowedRoles={['admin', 'responsable_stock']}>
              <SuiviColisPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/restock-alerts"
          element={
            <PrivateRoute allowedRoles={['admin', 'responsable_stock']}>
              <RestockAlertsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/receivables"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <ReceivablesDashboardPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/depot-dashboard"
          element={
            <PrivateRoute allowedRoles={['admin', 'responsable_stock', 'magasinier']}>
              <DepotDashboardPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/catalog-public"
          element={
            <PrivateRoute allowedRoles={['admin', 'responsable_stock']}>
              <CatalogPublicPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/analytics"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <AnalyticsDashboardPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/deposits"
          element={
            <PrivateRoute allowedRoles={['admin', 'comptable', 'coordinateur_zone']}>
              <DepositsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/order-approvals"
          element={
            <PrivateRoute allowedRoles={['admin', 'comptable']}>
              <OrderApprovalsPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/approvals"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <ApprovalRequestsPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/operational-reports"
          element={
            <PrivateRoute allowedRoles={['admin', 'coordinateur_general', 'coordinateur_zone', 'livreur']}>
              <OperationalReportsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/catalogue"
          element={
            <PrivateRoute allowedRoles={['admin', 'comptable', 'manager', 'coordinateur_general', 'coordinateur_zone', 'livreur', 'responsable_stock']}>
              <CataloguePage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/profile"
          element={
            <PrivateRoute allowedRoles={['admin', 'comptable', 'manager', 'coordinateur_general', 'coordinateur_zone', 'livreur', 'responsable_stock', 'magasinier', 'affilié', 'vendeur_affilié', 'manager_affilié']}>
              <ProfileManagementPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/sales-analytics"
          element={
            <PrivateRoute allowedRoles={['admin', 'manager', 'coordinateur_general', 'coordinateur_zone']}>
              <SalesAnalyticsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/caisses"
          element={
            <PrivateRoute allowedRoles={['admin', 'comptable']}>
              <CaissesPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/banques"
          element={
            <PrivateRoute allowedRoles={['admin', 'comptable']}>
              <BanquesPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/audit"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <AuditLogPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/ad-budgets"
          element={
            <PrivateRoute allowedRoles={['admin', 'coordinateur_general']}>
              <AdBudgetPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/sms-campaigns"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <SMSCampaignsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/commission-payments"
          element={
            <PrivateRoute allowedRoles={['admin', 'comptable']}>
              <CommissionPaymentsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/manual-deliveries"
          element={
            <PrivateRoute allowedRoles={['admin', 'responsable_stock', 'coordinateur_zone']}>
              <ManualDeliveriesPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/payroll"
          element={
            <PrivateRoute allowedRoles={['admin', 'comptable']}>
              <PayrollPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/weekly-reports"
          element={
            <PrivateRoute allowedRoles={['admin', 'comptable']}>
              <WeeklyReportsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/chat"
          element={
            <PrivateRoute allowedRoles={['admin', 'comptable', 'manager', 'coordinateur_general', 'coordinateur_zone', 'livreur', 'responsable_stock']}>
              <ChatPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/vip-discount"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <VIPDiscountPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/seller-discounts"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <SellerDiscountDashboardPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/seller-order-tracking"
          element={
            <PrivateRoute allowedRoles={['admin', 'manager', 'coordinateur_general', 'coordinateur_zone']}>
              <SellerOrderTrackingPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/payslip-settings"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <PayslipSettingsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/payslip-archive"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <PayslipArchivePage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/updates-management"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <UpdatesManagementPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/push-notifications"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <PushNotificationsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/stock-prediction"
          element={
            <PrivateRoute allowedRoles={['admin', 'responsable_stock', 'coordinateur_general']}>
              <StockPredictionPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/performance"
          element={
            <PrivateRoute allowedRoles={['admin', 'coordinateur_general']}>
              <PerformanceDashboardPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/media-creatif"
          element={
            <PrivateRoute allowedRoles={['admin', 'manager', 'manager_affiliated']}>
              <MediaCreatifPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/ma-boutique"
          element={
            <PrivateRoute allowedRoles={['admin', 'manager', 'manager_affiliated']}>
              <MaBoutiquePage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/loyalty"
          element={
            <PrivateRoute allowedRoles={['admin', 'manager', 'comptable']}>
              <LoyaltyPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/accounting-ohada"
          element={
            <PrivateRoute allowedRoles={['admin', 'comptable']}>
              <AccountingOhadaPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/kpi-dashboard"
          element={
            <PrivateRoute allowedRoles={['admin', 'comptable', 'coordinateur_general']}>
              <KPIDashboardPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/admin-dashboard"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <AdminDashboardPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/advertising-budget"
          element={
            <PrivateRoute allowedRoles={['admin', 'manager']}>
              <AdvertisingBudgetPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/smart-catalog"
          element={
            <PrivateRoute allowedRoles={['admin', 'manager', 'manager_affiliate', 'coordinateur_zone', 'coordinateur_general']}>
              <SmartCatalogPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/my-dashboard"
          element={
            <PrivateRoute allowedRoles={['admin', 'manager', 'seller', 'vendeur', 'coordinateur_zone', 'coordinateur_general', 'manager_affiliate', 'affiliate']}>
              <SellerDashboardPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/seller-dashboard"
          element={
            <PrivateRoute allowedRoles={['admin', 'manager', 'seller', 'vendeur', 'coordinateur_zone', 'coordinateur_general', 'manager_affiliate', 'affiliate']}>
              <SellerDashboardPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/feedback-management"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <FeedbackManagementPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/ai-tools"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <AIToolsPage />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/admin-documents"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <AdminDocumentsPage />
            </PrivateRoute>
          }
        />
        
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
      <Toaster position="top-right" richColors />
      <UpdateAlertModal />
      <PWAInstallPrompt />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <PermissionsProvider>
        <NotificationProvider>
          <AppSyncProvider>
            <BrowserRouter>
              <TimeCheckAlert />
              <AppContent />
            </BrowserRouter>
          </AppSyncProvider>
        </NotificationProvider>
      </PermissionsProvider>
    </AuthProvider>
  );
}

export default App;
